import React, { useCallback, useEffect, useState, JSX, ChangeEvent } from 'react';
import { PaginationItem, Pagination, PaginationLink } from 'reactstrap';
import styled from '@emotion/styled';
import { parseInt } from 'lodash-es';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/service-list.en.t.json';
import Keys from '../../../_Translations/generated/service-list.en.t.json.keys';

export interface PaginationProps {
  index: number;
  amount: number;
  currentPage: number;
  totalCount: number;
  onAmountChange: (amount: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onFirst: () => void;
  onLast: () => void;
  onPageNumber: (pageNumber: number) => void;
  hidden?: boolean;
  showing?: number;
}

const PaginationInput = styled.input`
  max-width: 45px;
`;

const PaginationContainer = styled.div`
  text-align: center;
  padding-top: 1em;
`;

const First = ({
  handleFirst,
  currentPageValue
}: {
  currentPageValue: number;
  handleFirst: (currentPageVal: number) => void;
}): JSX.Element => {
  return (
    <PaginationItem disabled={currentPageValue === 1}>
      <PaginationLink first onClick={(): void => handleFirst(currentPageValue)} />
    </PaginationItem>
  );
};

const Last = ({
  handleLast,
  pageCount,
  currentPageValue
}: {
  currentPageValue: number;
  pageCount: number;
  handleLast: (currentPageVal: number) => void;
}): JSX.Element => {
  return (
    <PaginationItem disabled={currentPageValue === pageCount}>
      <PaginationLink last onClick={(): void => handleLast(currentPageValue)} />
    </PaginationItem>
  );
};

const CustomPagination = (props: PaginationProps): JSX.Element => {
  const {
    index,
    amount,
    currentPage = 1,
    totalCount,
    showing,
    onAmountChange,
    onNext,
    onPrev,
    onFirst,
    onLast,
    onPageNumber
  } = props;

  const [pageCount, setPageCount] = useState<number>(Math.ceil(totalCount / amount));
  const [pageAmount, setPageAmount] = useState<number>(amount || 0);
  const [pageIndex, setPageIndex] = useState<number>(index);
  const [resultsPerPage, setResultPerPage] = useState<string>(showing?.toString() || '30');
  const [currentPageValue, setCurrentPageValue] = useState<number>(currentPage);
  const { t } = useTranslation('service-list', fallback, Keys);

  const handlePrev = useCallback(
    (currentPageVal: number): void => {
      if (currentPageVal > 1) {
        setCurrentPageValue(currentPageVal - 1);
        setPageIndex(prevState => prevState - pageAmount);
      }
      onPrev();
    },
    [onPrev, pageAmount]
  );

  const handlePaginationAmountChange = useCallback((): void => {
    const parsedResults = parseInt(resultsPerPage, 10);
    if (parsedResults && parsedResults > 0) {
      setPageAmount(parsedResults);
      setPageCount(Math.ceil(totalCount / parsedResults));
      setCurrentPageValue(Math.ceil((pageIndex + 1) / parsedResults));
      onAmountChange(parsedResults);
    }
  }, [onAmountChange, pageIndex, resultsPerPage, totalCount]);

  useEffect(() => {
    handlePaginationAmountChange();
  }, [handlePaginationAmountChange, totalCount]);

  const handleNext = useCallback(
    (currentPageVal: number, count: number): void => {
      if (currentPageVal < count) {
        setCurrentPageValue(currentPageVal + 1);
        setPageIndex(prevState => prevState + pageAmount);
      }
      onNext();
    },
    [onNext, pageAmount]
  );

  const handleFirst = useCallback((currentPageVal: number): void => {
    if (currentPageVal !== 1) {
      setCurrentPageValue(1);
      setPageIndex(0);
    }
    onFirst();
  }, []);

  const handleLast = useCallback(
    (currentPageVal: number): void => {
      if (currentPageVal !== pageCount) {
        setCurrentPageValue(pageCount);
        setPageIndex((pageCount - 1) * pageAmount);
      }
      onLast();
    },
    [onLast, pageAmount, pageCount]
  );

  const handlePageNumber = useCallback(
    (currentPageVal: number, newPage: number): void => {
      if (currentPageVal !== newPage) {
        setCurrentPageValue(newPage);
        setPageIndex((newPage - 1) * pageAmount);
      }
      onPageNumber(newPage);
    },
    [pageAmount, onPageNumber]
  );

  return (
    <>
      <PaginationContainer>
        <p>
          {t(Keys.results_per_page)}
          <PaginationInput
            className="custom-pagination-input form-control d-inline-block"
            onBlur={handlePaginationAmountChange}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setResultPerPage(e.target.value);
            }}
            value={resultsPerPage}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
              if (event.keyCode === 13) {
                handlePaginationAmountChange();
              }
            }}
            aria-label={t(Keys.results_per_page)}
          />{' '}
          {t(Keys.hits)} {totalCount}
        </p>
      </PaginationContainer>
      <Pagination listClassName="justify-content-center">
        <First handleFirst={handleFirst} currentPageValue={currentPageValue} />
        <PaginationItem disabled={currentPageValue === 1}>
          <PaginationLink previous onClick={(): void => handlePrev(currentPageValue)} />
        </PaginationItem>
        {pageCount <= 8 && (
          <>
            {[...Array(pageCount).keys()].map(idx => (
              <PaginationItem key={idx} active={idx + 1 === currentPageValue}>
                <PaginationLink
                  onClick={(): void => handlePageNumber(currentPageValue, idx + 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {idx + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
          </>
        )}
        {pageCount > 8 && (
          <>
            {currentPageValue !== 1 && currentPageValue !== 2 && (
              <PaginationItem key={1} active={currentPageValue === 1}>
                <PaginationLink
                  onClick={(): void => handlePageNumber(currentPageValue, 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {1}
                </PaginationLink>
              </PaginationItem>
            )}
            {currentPageValue > 3 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            {currentPageValue !== 1 && (
              <PaginationItem
                key={currentPageValue - 1}
                active={currentPageValue - 1 === currentPageValue}
              >
                <PaginationLink
                  onClick={(): void => handlePageNumber(currentPageValue, currentPageValue - 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {currentPageValue - 1}
                </PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem active>
              <PaginationLink style={{ cursor: 'pointer' }}>{currentPageValue}</PaginationLink>
            </PaginationItem>
            {currentPageValue !== pageCount && (
              <PaginationItem
                key={currentPageValue + 1}
                active={currentPageValue + 1 === currentPageValue}
              >
                <PaginationLink
                  onClick={(): void => handlePageNumber(currentPageValue, currentPageValue + 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {currentPageValue + 1}
                </PaginationLink>
              </PaginationItem>
            )}
            {currentPageValue < pageCount - 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            {currentPageValue !== pageCount && currentPageValue !== pageCount - 1 && (
              <PaginationItem key={pageCount} active={currentPageValue === pageCount}>
                <PaginationLink
                  onClick={(): void => handlePageNumber(currentPageValue, pageCount)}
                  style={{ cursor: 'pointer' }}
                >
                  {pageCount}
                </PaginationLink>
              </PaginationItem>
            )}
          </>
        )}
        <PaginationItem disabled={currentPageValue === pageCount}>
          <PaginationLink
            next
            disabled={currentPageValue === pageCount}
            onClick={(): void => handleNext(currentPageValue, pageCount)}
          />
        </PaginationItem>
        <Last handleLast={handleLast} currentPageValue={currentPageValue} pageCount={pageCount} />
      </Pagination>
    </>
  );
};
export default CustomPagination;
